import { Accordion, Box, Group, Popover, Stack } from '@mantine/core'
import { useTimeout } from '@mantine/hooks'
import React, { useState } from 'react'
import styled from 'styled-components'
import Icon from 'components/Icon'
import { Image } from 'components/Image'
import { ToggleButton } from 'components/ToggleButton'
import { Text } from 'components/Typography'
import { SPACE, FONT_WEIGHT, RADIUS, FONT_SIZE, COLOR_INTENT } from 'Theme'
import { type PublicProductQuery } from 'types/graphql-generated'
import { useMedia } from 'useMedia'
import { type TypesenseProductVariantV1 } from 'utils/typesenseClient'
import {
  convertOptionValuesToOptions,
  getValuesForOption,
  isVariantsOptionUnvailable,
} from '../../utils'
import { VariantAccordion } from './VariantAccordion'

const VARIANT_IMAGE_WIDTH = SPACE.PX_36

const VariantImage = styled(Image)`
  border-radius: ${RADIUS.CIRCLE};
  width: ${VARIANT_IMAGE_WIDTH};
`

const SELECTED_VARIANT_IMAGE_WIDTH = SPACE.PX_22

const SelectedVariantImage = styled(Image)`
  border-radius: ${RADIUS.CIRCLE};
  width: ${SELECTED_VARIANT_IMAGE_WIDTH};
`

const SAMPLE_IMAGE_WIDTH = SPACE.PX_120

const SampleImage = styled(Image)`
  border-radius: ${RADIUS.CIRCLE};
  width: ${SAMPLE_IMAGE_WIDTH};
`

interface ImageData {
  url?: string
  alt?: string
}

interface VariantData {
  fabricHeader?: string
  fabricSubheader?: string
  fabricDetails?: string
  fabricImage?: ImageData
  finishHeader?: string
  finishSubheader?: string
  finishDetails?: string
  finishImage?: ImageData
  colorImage?: ImageData
  options: Record<string, string>
}

const getFabricImageProps = (material: string, fabricImage: ImageData) => {
  const isCustomerOwnMaterial = material === 'COM'
  const imageUrl = isCustomerOwnMaterial
    ? 'https://theexpert-production.b-cdn.net/com-icon.svg'
    : fabricImage?.url
  const imageAlt = isCustomerOwnMaterial
    ? "Customer's Own Material (COM)"
    : fabricImage?.alt
  return { isCustomerOwnMaterial, imageUrl, imageAlt }
}

const VariantSample = ({
  label,
  imageUrl,
  imageAlt,
  onClick,
  onMouseEnter,
  onMouseLeave,
  subheader,
  isSelected,
  isUnavailable,
}: {
  label: string
  imageUrl?: string
  imageAlt?: string
  onClick: React.MouseEventHandler<HTMLDivElement>
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>
  subheader: string
  isSelected: boolean
  isUnavailable: boolean
}) => {
  let borderColor = 'transparent'

  if (isSelected) {
    borderColor = COLOR_INTENT.VARIANT_PICKER.SELECTION_RING_COLOR
  } else if (isUnavailable) {
    borderColor = COLOR_INTENT.VARIANT_PICKER.DISABLED_RING_COLOR
  }

  return (
    <Box
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        borderWidth: '2px',
        height: 48,
        borderStyle: 'solid',
        borderRadius: RADIUS.CIRCLE,
        cursor: isUnavailable ? 'not-allowed' : 'pointer',
        padding: SPACE.PX_4,
        borderColor,
        position: 'relative',
      }}
      onClick={isUnavailable ? undefined : onClick}
      aria-label={`${subheader} ${label}`}
    >
      <VariantImage
        baseWidth={36}
        src={imageUrl}
        alt={imageAlt}
        isDisabled={isUnavailable}
      />
      {isUnavailable && (
        <Box
          pos="absolute"
          top={0}
          bottom={0}
          left={0}
          right={0}
          m={6}
          style={{
            background: `linear-gradient(to top right, rgba(0,0,0,0) 0%, rgba(0,0,0,0) calc(50% - 1.5px), rgba(1,1,1,0.1) 50%, rgba(0,0,0,0) calc(50% + 1.5px), rgba(0,0,0,0) 100%)`,
          }}
        />
      )}
    </Box>
  )
}

type VariantPopoverProps = {
  imageUrl?: string
  imageAlt?: string
  subHeader: string
  details: string
  header: string
}

const VariantGroup = ({
  children,
  ...rest
}: React.ComponentProps<typeof Group>) => (
  <Group gap={SPACE.PX_8} maw={60 * 8} {...rest}>
    {children}
  </Group>
)

const OVERLAY_TIMEOUT = 200

interface VariantGroupProps {
  selectedOptionValues: Record<string, string>
  variants: VariantData[]
  onVariantChange: (
    optionName: string,
    value: string,
    isUnavailable: boolean
  ) => void
}

const VariantFabricHeaderGroup = ({
  header,
  headerVariants,
  onMouseEnter,
  onMouseLeave,
  selectedMaterialOption,
  selectedVariant,
  selectedOptionValues,
  variants,
  onVariantChange,
  ...rest
}: VariantGroupProps & {
  headerVariants: Map<string, VariantData>
  onMouseEnter: (props: VariantPopoverProps) => void
  onMouseLeave: () => void
  selectedMaterialOption: string
  selectedVariant: VariantData
  header: string
}) => {
  const { start, clear } = useTimeout(() => onMouseLeave(), OVERLAY_TIMEOUT)

  return (
    <Stack gap={SPACE.PX_18}>
      <Stack gap={SPACE.PX_10}>
        <Text
          color={COLOR_INTENT.VARIANT_PICKER.SUBGROUP_COLOR}
          fontSize={FONT_SIZE.PX_12}
          fontStyle="italic"
        >
          {header === 'COM' ? "Customer's Own Material (COM)" : header}
        </Text>
        <VariantGroup>
          {Array.from(headerVariants).map(
            ([material, { fabricImage, fabricDetails, fabricSubheader }]) => {
              const { isCustomerOwnMaterial, imageUrl, imageAlt } =
                getFabricImageProps(material, fabricImage)

              const isUnavailable = isVariantsOptionUnvailable(
                {
                  name: 'material',
                  value: material,
                },
                selectedOptionValues,
                variants
              )

              return (
                <VariantSample
                  key={material}
                  imageUrl={imageUrl}
                  imageAlt={imageAlt}
                  label={
                    material === 'COM'
                      ? "Customer's Own Material (COM)"
                      : material
                  }
                  onClick={() =>
                    onVariantChange('material', material, isUnavailable)
                  }
                  subheader={fabricSubheader}
                  isSelected={
                    (isCustomerOwnMaterial ||
                      header === selectedVariant.fabricHeader) &&
                    material === selectedMaterialOption
                  }
                  isUnavailable={isUnavailable}
                  onMouseEnter={() => {
                    clear()

                    onMouseEnter({
                      imageUrl,
                      imageAlt,
                      subHeader: fabricSubheader,
                      details: fabricDetails,
                      header: isCustomerOwnMaterial
                        ? "Customer's Own Material (COM)"
                        : material,
                    })
                  }}
                  onMouseLeave={() => start()}
                  {...rest}
                />
              )
            }
          )}
        </VariantGroup>
      </Stack>
    </Stack>
  )
}

const VariantFinishGroup = ({
  finishes,
  onVariantChange,
  onMouseEnter,
  onMouseLeave,
  selectedFinishOption,
  selectedOptionValues,
  variants,
}: VariantGroupProps & {
  finishes: Array<[string, VariantData]>
  onMouseEnter: (props: VariantPopoverProps) => void
  onMouseLeave: () => void
  selectedFinishOption: string
}) => {
  const { start, clear } = useTimeout(() => onMouseLeave(), OVERLAY_TIMEOUT)

  return (
    <VariantGroup>
      {finishes.map(([finish, variant]) => {
        const isUnavailable = isVariantsOptionUnvailable(
          {
            name: 'finish',
            value: finish,
          },
          selectedOptionValues,
          variants
        )

        return (
          <VariantSample
            key={finish}
            imageUrl={variant.finishImage?.url}
            imageAlt={variant.finishImage?.alt}
            label={finish}
            onClick={() => onVariantChange('finish', finish, isUnavailable)}
            subheader={variant.finishSubheader}
            isSelected={finish === selectedFinishOption}
            isUnavailable={isUnavailable}
            onMouseEnter={() => {
              clear()

              onMouseEnter({
                imageUrl: variant.finishImage?.url,
                imageAlt: variant.finishImage?.alt,
                subHeader: variant.finishSubheader,
                details: variant.finishDetails,
                header: finish,
              })
            }}
            onMouseLeave={() => start()}
          />
        )
      })}
    </VariantGroup>
  )
}

const VariantColorGroup = ({
  colors,
  onVariantChange,
  selectedColorOption,
  selectedOptionValues,
  variants,
}: VariantGroupProps & {
  colors: Array<[string, VariantData]>
  selectedColorOption: string
}) => (
  <VariantGroup>
    {colors.map(([color, variant]) => {
      const isUnavailable = isVariantsOptionUnvailable(
        {
          name: 'color',
          value: color,
        },
        selectedOptionValues,
        variants
      )

      return (
        <VariantSample
          key={color}
          imageUrl={variant.colorImage?.url}
          imageAlt={variant.colorImage?.alt}
          label={color}
          subheader={variant.finishSubheader}
          isSelected={color === selectedColorOption}
          isUnavailable={isUnavailable}
          onClick={() => onVariantChange('color', color, isUnavailable)}
        />
      )
    })}
  </VariantGroup>
)

const AccordionHeaderText = ({
  children,
  ...rest
}: React.ComponentProps<typeof Text>) => (
  <Text
    color={COLOR_INTENT.VARIANT_PICKER.GROUP_COLOR}
    fontSize={FONT_SIZE.PX_13}
    fontWeight={FONT_WEIGHT.MEDIUM}
    letterSpacing="0.1em"
    {...rest}
  >
    {children}
  </Text>
)

export interface VariantDetailAccordionProps {
  variants?: VariantData[]
  selectedVariant?: VariantData
  onVariantChange: (
    optionName: string,
    value: string,
    isUnavailable: boolean
  ) => void
}

const VariantPickerBase: React.FC<React.PWC<VariantDetailAccordionProps>> = ({
  selectedVariant,
  variants = [],
  onVariantChange,
}) => {
  const selectedMaterialOption = selectedVariant?.options.material

  const isFabricSectionEnabled =
    selectedMaterialOption === 'COM' ||
    variants.some(
      ({ fabricHeader, fabricSubheader, fabricDetails, fabricImage }) =>
        !!fabricHeader || !!fabricSubheader || !!fabricDetails || !!fabricImage
    )
  const isFinishSectionEnabled = variants.some(
    ({ finishHeader, finishSubheader, finishDetails, finishImage }) =>
      !!finishHeader || !!finishSubheader || !!finishDetails || !!finishImage
  )
  const isColorSectionEnabled = variants.some(({ colorImage }) => !!colorImage)

  const [hoveringVariantProps, setHoveringVariantProps] =
    useState<VariantPopoverProps>(null)
  const { MOBILE } = useMedia()

  const selectedOptionValues = selectedVariant?.options
  const fabricHeaderGroups = new Map<string, Map<string, VariantData>>()
  const finishes = new Map<string, VariantData>()
  const colors = new Map<string, VariantData>()
  const selectedFinishOption = selectedVariant?.options.finish
  const selectedColorOption = selectedVariant?.options.color

  const options = variants.reduce((acc, variant) => {
    Object.keys(variant.options).forEach((name) => {
      acc.add(name)
    })

    return acc
  }, new Set<string>())

  let availableOptions = Array.from(options).filter(
    (optionName) => getValuesForOption(optionName, variants).length > 1
  )

  let preopenOptions = availableOptions.map((name) => name)
  if (isFabricSectionEnabled) {
    preopenOptions = ['fabric']
  } else if (isColorSectionEnabled) {
    preopenOptions = ['color']
  } else if (isFinishSectionEnabled) {
    preopenOptions = ['finish']
  }

  const [value, setValue] = useState<string | string[]>(preopenOptions)

  if (isFabricSectionEnabled) {
    availableOptions = availableOptions.filter((name) => name !== 'material')
  }

  if (isFinishSectionEnabled) {
    availableOptions = availableOptions.filter((name) => name !== 'finish')
  }

  if (isColorSectionEnabled) {
    availableOptions = availableOptions.filter((name) => name !== 'color')
  }

  variants.forEach((variant) => {
    const { material, finish, color } = variant.options

    if (material) {
      const fabricHeader = material === 'COM' ? 'COM' : variant.fabricHeader
      if (!fabricHeaderGroups.has(fabricHeader)) {
        fabricHeaderGroups.set(fabricHeader, new Map<string, VariantData>())
      }

      fabricHeaderGroups.get(fabricHeader).set(material, variant)
    }

    if (finish) {
      finishes.set(finish, variant)
    }

    if (color) {
      colors.set(color, variant)
    }
  })

  const sortedFabricHeaderGroups = Array.from(fabricHeaderGroups).sort(
    (a, b) => {
      // COM should always be last
      if (a[0] === 'COM') {
        return 1
      }
      if (b[0] === 'COM') {
        return -1
      }

      const aVariantCount = [...a[1].values()].length
      const bVariantCount = [...b[1].values()].length
      return bVariantCount - aVariantCount // Sort by number of variants
    }
  )

  const sortedFinishes = Array.from(finishes).sort(
    (a, b) => a[0].localeCompare(b[0]) // Sort by name
  )

  const sortedColors = Array.from(colors).sort(
    (a, b) => a[0].localeCompare(b[0]) // Sort by name
  )

  const selectedFabricVariantImageProps = getFabricImageProps(
    selectedMaterialOption,
    selectedVariant?.fabricImage
  )

  return (
    <>
      {!MOBILE && (
        <Popover
          width={320}
          position="left"
          shadow="md"
          opened={hoveringVariantProps !== null}
        >
          <Popover.Target>
            <Box />
          </Popover.Target>
          <Popover.Dropdown style={{ pointerEvents: 'none' }}>
            {hoveringVariantProps && (
              <Stack p={SPACE.PX_32} pb={SPACE.PX_40} gap={SPACE.PX_5}>
                <Box w={132}>
                  <SampleImage
                    src={hoveringVariantProps.imageUrl}
                    alt={hoveringVariantProps.imageAlt}
                  />
                </Box>
                <Text
                  color={COLOR_INTENT.VARIANT_PICKER.GROUP_COLOR}
                  fontSize={FONT_SIZE.PX_13}
                  style={{ textTransform: 'uppercase' }}
                  letterSpacing="0.1em"
                  fontWeight={FONT_WEIGHT.MEDIUM}
                  mt={SPACE.PX_20}
                >
                  {hoveringVariantProps.subHeader}
                </Text>
                <Text
                  color={COLOR_INTENT.VARIANT_PICKER.SELECTED_VARIANT_COLOR}
                  fontSize={FONT_SIZE.PX_12}
                >
                  {hoveringVariantProps.header}
                </Text>
                <Text
                  color={COLOR_INTENT.VARIANT_PICKER.GROUP_COLOR}
                  fontSize={FONT_SIZE.PX_14}
                  mt={SPACE.PX_10}
                >
                  {hoveringVariantProps.details}
                </Text>
              </Stack>
            )}
          </Popover.Dropdown>
        </Popover>
      )}
      <VariantAccordion
        value={value}
        onChange={setValue}
        multiple
        defaultValue={['finish']}
      >
        {isFabricSectionEnabled && (
          <Accordion.Item value="fabric">
            <Accordion.Control
              chevron={
                value.includes('fabric') ? <Icon.Minus /> : <Icon.Plus />
              }
            >
              <Group align="center" gap={SPACE.PX_18}>
                <AccordionHeaderText>FABRIC</AccordionHeaderText>
                <SelectedVariantImage
                  baseWidth={22}
                  src={selectedFabricVariantImageProps.imageUrl}
                  alt={selectedFabricVariantImageProps.imageAlt}
                />
                <Text
                  color={COLOR_INTENT.VARIANT_PICKER.SELECTED_VARIANT_COLOR}
                  fontSize={FONT_SIZE.PX_12}
                >
                  <Group>
                    {selectedVariant?.fabricSubheader
                      ? `${selectedVariant.fabricSubheader} - `
                      : ''}
                    {selectedMaterialOption === 'COM'
                      ? "Customer's Own Material (COM)"
                      : selectedMaterialOption}
                  </Group>
                </Text>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap={SPACE.PX_12}>
                {MOBILE && (
                  <Text fontSize={FONT_SIZE.PX_14}>
                    {selectedVariant?.fabricDetails}
                  </Text>
                )}
                <Stack>
                  {Array.from(sortedFabricHeaderGroups).map(
                    ([fabricHeader, headerVariants]) => (
                      <VariantFabricHeaderGroup
                        key={fabricHeader}
                        header={fabricHeader}
                        headerVariants={headerVariants}
                        selectedMaterialOption={selectedMaterialOption}
                        selectedVariant={selectedVariant}
                        selectedOptionValues={selectedOptionValues}
                        variants={variants}
                        onVariantChange={onVariantChange}
                        onMouseEnter={setHoveringVariantProps}
                        onMouseLeave={() => setHoveringVariantProps(null)}
                      />
                    )
                  )}
                </Stack>
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}
        {isColorSectionEnabled && (
          <Accordion.Item value="color">
            <Accordion.Control
              chevron={value.includes('color') ? <Icon.Minus /> : <Icon.Plus />}
            >
              <Group align="center" gap={SPACE.PX_18}>
                <AccordionHeaderText>COLOR</AccordionHeaderText>
                <SelectedVariantImage
                  baseWidth={22}
                  src={selectedVariant?.colorImage?.url}
                  alt={selectedVariant?.colorImage?.alt}
                />
                <Text
                  color={COLOR_INTENT.VARIANT_PICKER.SELECTED_VARIANT_COLOR}
                  fontSize={FONT_SIZE.PX_12}
                >
                  {selectedColorOption}
                </Text>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap={SPACE.PX_12}>
                <VariantColorGroup
                  colors={sortedColors}
                  selectedColorOption={selectedColorOption}
                  onVariantChange={onVariantChange}
                  selectedOptionValues={selectedOptionValues}
                  variants={variants}
                />
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}
        {isFinishSectionEnabled && (
          <Accordion.Item value="finish">
            <Accordion.Control
              chevron={
                value.includes('finish') ? <Icon.Minus /> : <Icon.Plus />
              }
            >
              <Group align="center" gap={SPACE.PX_18}>
                <AccordionHeaderText>FINISH</AccordionHeaderText>
                <SelectedVariantImage
                  baseWidth={22}
                  src={selectedVariant?.finishImage?.url}
                  alt={selectedVariant?.finishImage?.alt}
                />
                <Text
                  color={COLOR_INTENT.VARIANT_PICKER.SELECTED_VARIANT_COLOR}
                  fontSize={FONT_SIZE.PX_12}
                >
                  {selectedFinishOption}
                </Text>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap={SPACE.PX_12}>
                {MOBILE && (
                  <Text fontSize={FONT_SIZE.PX_14}>
                    {selectedVariant.finishDetails}
                  </Text>
                )}
                <VariantFinishGroup
                  finishes={sortedFinishes}
                  selectedFinishOption={selectedFinishOption}
                  onVariantChange={onVariantChange}
                  selectedOptionValues={selectedOptionValues}
                  variants={variants}
                  onMouseEnter={setHoveringVariantProps}
                  onMouseLeave={() => setHoveringVariantProps(null)}
                />
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}
        {availableOptions.map((optionName) => (
          <Accordion.Item key={optionName} value={optionName}>
            <Accordion.Control
              chevron={
                value.includes(optionName) ? <Icon.Minus /> : <Icon.Plus />
              }
            >
              <Group align="center" gap={SPACE.PX_18}>
                <AccordionHeaderText>
                  {optionName.toUpperCase()}
                </AccordionHeaderText>
                <Text
                  color={COLOR_INTENT.VARIANT_PICKER.SELECTED_VARIANT_COLOR}
                  fontSize={FONT_SIZE.PX_12}
                >
                  {selectedOptionValues[optionName]}
                </Text>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Group>
                {getValuesForOption(optionName, variants).map(
                  (optionValue, index) => (
                    <ToggleButton
                      value={optionValue}
                      key={`${optionName}${index}`}
                      groupValue={selectedOptionValues[optionName]}
                      onChange={({ target }, isUnavailable) => {
                        onVariantChange(optionName, target.value, isUnavailable)
                      }}
                      aria-label={optionValue}
                      isUnavailable={isVariantsOptionUnvailable(
                        {
                          name: optionName,
                          value: optionValue,
                        },
                        selectedOptionValues,
                        variants
                      )}
                    >
                      {optionValue}
                    </ToggleButton>
                  )
                )}
              </Group>
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </VariantAccordion>
    </>
  )
}

type LegacyProductVariantData = PublicProductQuery['product']['variants'][0]

export const VariantPickerLegacy: React.FC<
  React.PWC<
    Pick<VariantDetailAccordionProps, 'onVariantChange'> & {
      variants: LegacyProductVariantData[]
      selectedVariant: LegacyProductVariantData
    }
  >
> = ({ selectedVariant, variants, onVariantChange }) => {
  const baseVariants = variants.map(({ optionValues, ...rest }) => ({
    options: convertOptionValuesToOptions(optionValues),
    ...rest,
  }))

  return (
    <VariantPickerBase
      onVariantChange={onVariantChange}
      variants={baseVariants}
      selectedVariant={{
        ...selectedVariant,
        options: convertOptionValuesToOptions(selectedVariant.optionValues),
      }}
    />
  )
}

export const VariantPickerTypesense: React.FC<
  React.PWC<
    Pick<VariantDetailAccordionProps, 'onVariantChange'> & {
      variants: TypesenseProductVariantV1[]
      selectedVariant?: TypesenseProductVariantV1
    }
  >
> = ({ selectedVariant, variants, onVariantChange }) => (
  <VariantPickerBase
    onVariantChange={onVariantChange}
    variants={variants}
    selectedVariant={selectedVariant}
  />
)
